import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { playCheckInBell } from '../utils/bellSound';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/helpconnect/staff/message-alerts`;
const POLL_MS = 15000;
export const OPEN_MESSENGER_EVENT = 'myhelp:open-messenger';

// Pop-up for the assigned case manager when a participant sends a HELPConnect message.
export const HelpConnectMessageAlert = () => {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const shown = useRef(new Set());
  const current = queue[0];

  const check = useCallback(() => {
    if (!user) return;
    axios.get(API).then((r) => {
      const fresh = r.data.alerts.filter((a) => !shown.current.has(a.id));
      if (!fresh.length) return;
      fresh.forEach((a) => shown.current.add(a.id));
      setQueue((q) => [...q, ...fresh]);
      playCheckInBell();
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    const first = setTimeout(check, 3000);
    const poll = setInterval(check, POLL_MS);
    return () => { clearTimeout(first); clearInterval(poll); };
  }, [user, check]);

  if (!current) return null;
  const done = () => { axios.post(`${API}/${current.id}/shown`).catch(() => {}); setQueue((q) => q.slice(1)); };
  const open = () => { window.dispatchEvent(new CustomEvent(OPEN_MESSENGER_EVENT, { detail: current.conversation_id })); done(); };
  const preview = (current.message || '').replace(new RegExp(`^${(current.sender_name || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:\\s*`), '');
  return (
    <Dialog open onOpenChange={(o) => !o && done()}>
      <DialogContent className="max-w-md" data-testid="helpconnect-message-alert">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-primary" />{current.title || 'New HELPConnect message'}</DialogTitle>
          <DialogDescription data-testid="helpconnect-message-alert-from">From <b>{current.sender_name}</b></DialogDescription>
        </DialogHeader>
        {preview && <p className="text-sm rounded-lg border p-3 line-clamp-4 whitespace-pre-wrap" data-testid="helpconnect-message-alert-text">{preview}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={done} data-testid="helpconnect-message-alert-close-btn">Close</Button>
          <Button onClick={open} data-testid="helpconnect-message-alert-open-btn">Open</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
